/**
 * useFkLookupLabel — the display label of the row a lookup filter value points
 * at (`42` → `Acme Corp`), as the filter chip's lookup button shows it
 * (`42 — Acme Corp`). Shared by `FkLookupValueButton` and the Visual Query
 * Editor's WHERE / HAVING pills so both read the same row through the same
 * cached query (`useForeignKeyData`, keyed by target + value).
 *
 * The label reads the SAME declaration the lookup picker resolves for this
 * condition (by `sourcePath` among the column's loose FKs) — its target AND
 * its own target filter — so a column hosting several loose FKs never labels
 * through the first declaration's target. A real FK resolves no declaration:
 * the constraint wins. The persisted label column of the referenced table
 * (`tableDisplayColumns`) wins over the name heuristic; an explicit "none"
 * suppresses the label.
 *
 * Returns `null` while the row loads, when the value is empty, when no row
 * matches, or when the target has no label column.
 */

import { useMemo } from 'react';

import type { FilterGroup } from '../../core/types/workspaceTypes';
import { useForeignKeyData } from '../../table-explorer/hooks/useForeignKeyData';
import { pickDisplayColumn } from '../../table-explorer/utils/pickDisplayColumn';
import {
  findLooseRelationshipsForColumn,
  resolveActiveLooseRel,
  resolveActiveTargetFilter,
} from '../utils/looseFkResolver';
import { resolveTableDisplayColumn } from '../utils/tableDisplayColumn';
import { useMetaInfo } from './useMetaInfo';

export type FkLookupLabelArgs = {
  readonly connectionId: string;
  readonly database: string;
  readonly sourceColumn: string;
  readonly sourceSchema?: string;
  readonly sourceTable?: string;
  /** Empty string = no declared relationship (nothing to label). */
  readonly referencedSchema: string;
  readonly referencedTable: string;
  readonly referencedColumn: string;
  readonly referencesRealForeignKey?: boolean;
  /** The caller's target filter — used only when no loose declaration is active. */
  readonly targetFilter?: FilterGroup[];
  readonly sourcePath?: string;
  readonly value: unknown;
};

export function useFkLookupLabel({
  connectionId,
  database,
  sourceColumn,
  sourceSchema,
  sourceTable,
  referencedSchema,
  referencedTable,
  referencedColumn,
  referencesRealForeignKey = false,
  targetFilter,
  sourcePath,
  value,
}: FkLookupLabelArgs): string | null {
  const { data: metaInfo } = useMetaInfo(connectionId, database);

  const activeLooseRel = useMemo(() => {
    if (referencesRealForeignKey || sourceSchema === undefined || sourceTable === undefined) return null;
    return resolveActiveLooseRel(
      findLooseRelationshipsForColumn(metaInfo?.looseRelationships, sourceSchema, sourceTable, sourceColumn),
      sourcePath,
    );
  }, [metaInfo, sourceSchema, sourceTable, sourceColumn, sourcePath, referencesRealForeignKey]);
  const previewSchema = activeLooseRel?.toSchema ?? referencedSchema;
  const previewTable = activeLooseRel?.toTable ?? referencedTable;
  const previewColumn = activeLooseRel?.toColumn ?? referencedColumn;

  const fkData = useForeignKeyData({
    connectionId,
    database,
    referencedSchema: previewSchema,
    referencedTable: previewTable,
    referencedColumn: previewColumn,
    value,
    enabled: !!previewTable && !!previewColumn && value !== null && value !== undefined && value !== '',
    targetFilter: resolveActiveTargetFilter(activeLooseRel, targetFilter),
  });

  const columnNames = fkData.data?.columns.map((c) => c.name) ?? [];
  const persistedDisplayColumn = resolveTableDisplayColumn(metaInfo ?? null, previewSchema, previewTable, columnNames);
  const displayColumn =
    persistedDisplayColumn !== undefined ? persistedDisplayColumn : pickDisplayColumn(columnNames, previewColumn);

  const labelValue = displayColumn && fkData.data?.row ? fkData.data.row[displayColumn] : undefined;
  if (labelValue === null || labelValue === undefined) return null;
  return typeof labelValue === 'object' ? JSON.stringify(labelValue) : String(labelValue);
}
